/**
 * React Query hooks for parameter optimization.
 * Provides hooks for running optimizations and managing optimized parameters.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  getOptimizedParams,
  getAllOptimizations,
  deleteOptimization,
  deleteOptimizationById,
} from '../api/client';
import type {
  OptimizationResult,
} from '../types';

// Query keys for cache management
export const optimizationQueryKeys = {
  all: ['optimizations'] as const,
  optimized: (strategyName: string, symbol: string, timeframe: string) =>
    ['optimization', strategyName, symbol, timeframe] as const,
};

// ============================================================================
// Query Hooks
// ============================================================================

/**
 * Fetch all optimization runs for a specific strategy, symbol, and timeframe
 * Returns an array of results sorted by most recent first
 */
export function useOptimizedParams(strategyName: string, symbol: string, timeframe: string) {
  return useQuery<OptimizationResult[], Error>({
    queryKey: optimizationQueryKeys.optimized(strategyName, symbol, timeframe),
    queryFn: () => getOptimizedParams(strategyName, symbol, timeframe),
    enabled: !!strategyName && !!symbol && !!timeframe,
    staleTime: 1000 * 60 * 5, // 5 minutes - optimization history may change
    retry: false, // Don't retry on 404
  });
}

/**
 * Fetch all optimization results
 */
export function useAllOptimizations() {
  return useQuery<OptimizationResult[], Error>({
    queryKey: optimizationQueryKeys.all,
    queryFn: getAllOptimizations,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============================================================================
// Mutation Hooks
// ============================================================================

/**
 * Delete all optimization runs for a strategy/symbol/timeframe
 */
export function useDeleteOptimization() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { strategyName: string; symbol: string; timeframe: string }>({
    mutationFn: ({ strategyName, symbol, timeframe }) => deleteOptimization(strategyName, symbol, timeframe),
    onSuccess: (_, { strategyName, symbol, timeframe }) => {
      // Invalidate all optimizations list
      queryClient.invalidateQueries({ queryKey: optimizationQueryKeys.all });

      // Remove from cache
      queryClient.removeQueries({
        queryKey: optimizationQueryKeys.optimized(strategyName, symbol, timeframe),
      });
    },
  });
}

/**
 * Delete a specific optimization run by ID
 */
export function useDeleteOptimizationById() {
  const queryClient = useQueryClient();

  return useMutation<void, Error, { id: string; strategyName: string; symbol: string; timeframe: string }>({
    mutationFn: ({ id }) => deleteOptimizationById(id),
    onSuccess: (_, { strategyName, symbol, timeframe }) => {
      // Invalidate all optimizations list
      queryClient.invalidateQueries({ queryKey: optimizationQueryKeys.all });

      // Invalidate the specific optimization history so it refetches
      queryClient.invalidateQueries({
        queryKey: optimizationQueryKeys.optimized(strategyName, symbol, timeframe),
      });
    },
  });
}
